import { Flex, Spinner } from "@radix-ui/themes";
import React, { memo, useMemo } from "react";
import { Image as KonvaImage, Layer, Line, Stage } from "react-konva";
import { useMeasure } from "react-use";
import useImage from "use-image";
import { polygonProps } from "@/entities/planogram";
import { getBoundingSquare } from "./helpers/getBoundingSquare";

interface PolygonCropProps {
  imageUrl: string;
  originalWidth: number;
  originalHeight: number;
  polygonPoints: [number, number][];
}

const PolygonCrop: React.FC<PolygonCropProps> = memo(
  ({ imageUrl, ...props }) => {
    const [image, status] = useImage(imageUrl);
    const [ref, { width: currentSize }] = useMeasure<HTMLDivElement>();

    return (
      <div ref={ref} className="rounded overflow-hidden w-full h-full">
        {status === "loading" ? (
          <Flex
            className="w-full h-full bg-gray-200"
            justify="center"
            align="center"
          >
            <Spinner />
          </Flex>
        ) : status === "loaded" ? (
          <PolygonCropCanvas
            currentSize={currentSize}
            image={image}
            {...props}
          />
        ) : null}
      </div>
    );
  }
);

function PolygonCropCanvas({
  image,
  originalWidth,
  originalHeight,
  polygonPoints,
  currentSize,
}: Omit<PolygonCropProps, "imageUrl"> & {
  image: HTMLImageElement | undefined;
  currentSize: number;
}) {
  const poly = polygonProps();

  const boundingSquare = useMemo(() => {
    return getBoundingSquare(polygonPoints, originalWidth, originalHeight, 0.1);
  }, [polygonPoints]);

  const adjust = (vertex: number) => {
    // fit original vertex to current canvas size
    // example: (120 / 310) * 110
    return (vertex / boundingSquare.size) * currentSize;
  };

  const flatPoints = useMemo(() => {
    return polygonPoints
      .map(([x, y]) => {
        const sourceX = x * originalWidth - boundingSquare.x;
        const sourceY = y * originalHeight - boundingSquare.y;

        const adjustedWidth = adjust(sourceX);
        const adjustedHeight = adjust(sourceY);

        return [adjustedWidth, adjustedHeight];
      })
      .flatMap((point) => point);
  }, [polygonPoints, boundingSquare]);

  const scaleRatio = boundingSquare.size / currentSize;

  return (
    <Stage width={currentSize} height={currentSize}>
      <Layer>
        <KonvaImage
          clipWidth={boundingSquare.size}
          clipHeight={boundingSquare.size}
          x={-boundingSquare.x / scaleRatio}
          y={-boundingSquare.y / scaleRatio}
          width={originalWidth / scaleRatio}
          height={originalHeight / scaleRatio}
          image={image}
        />
        <Line
          shadowForStrokeEnabled={false}
          stroke={poly.stroke().yellow()}
          strokeWidth={2}
          closed
          hitStrokeWidth={0}
          points={flatPoints}
        />
      </Layer>
    </Stage>
  );
}

export default PolygonCrop;
